import React, { useState } from "react";
import { MessageSquarePlus, X, Star, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Floating feedback widget — Swiss/Editorial mobile-first per /app/design_guidelines.json.
 *
 * Radius hierarchy: outer card rounded-[32px] mobile / rounded-[40px] desktop;
 *                   inputs rounded-2xl; buttons + chips rounded-full.
 * Header: absolute close button so the editorial title gets full width.
 *         Eyebrow kicker "FEEDBACK" + tight bold heading.
 * Stars: justify-center gap-3 (not justify-between — that scattered look on
 *        narrow phones was the original complaint).
 * Inputs: crisp white + border-zinc-200 (not muddy gray fill).
 * Sticky CTA: gradient fade-in from-white via-white/95, button h-14 with bold
 *             text-base centered.
 */
const CATEGORIES = [
  { key: "compliment", label: "💖 Compliment" },
  { key: "feature", label: "✨ Feature" },
  { key: "bug", label: "🐛 Bug" },
  { key: "complaint", label: "⚠️ Complaint" },
  { key: "other", label: "💬 Other" },
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [category, setCategory] = useState("compliment");
  const [message, setMessage] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Auto-open via deeplink (post-delivery email pings users here).
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("fb") === "open") {
      setOpen(true);
      const order = params.get("order");
      if (order) setMessage(`[Order ${order}] `);
      const url = new URL(window.location.href);
      url.searchParams.delete("fb");
      url.searchParams.delete("order");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const reset = () => {
    setRating(0); setHover(0); setCategory("compliment");
    setMessage(""); setContactName(""); setContactEmail("");
    setSubmitted(false); setError(null);
  };
  const close = () => { setOpen(false); setTimeout(reset, 250); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!rating) { setError("Please pick a star rating"); return; }
    if (message.trim().length < 2) { setError("Add a short message"); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/feedback", {
        rating,
        category,
        message: message.trim(),
        page_url: typeof window !== "undefined" ? window.location.pathname : null,
        contact_name: contactName.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Trigger — fixed bottom-right */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        data-testid="feedback-trigger-btn"
        className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full bg-[#FF5722] text-white shadow-[0_8px_24px_rgba(255,87,34,0.4)] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      >
        <MessageSquarePlus size={22} />
      </button>

      {/* Overlay + floating card */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
          onClick={close}
          data-testid="feedback-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed left-4 right-4 bottom-4 w-auto max-h-[85vh] bg-white rounded-[32px] shadow-[0_8px_40px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden md:inset-auto md:bottom-auto md:left-1/2 md:top-1/2 md:right-auto md:-translate-x-1/2 md:-translate-y-1/2 md:w-[480px] md:rounded-[40px] md:max-h-[85vh] animate-in slide-in-from-bottom-8 md:slide-in-from-bottom-0 md:fade-in duration-300"
          >
            {/* Drag handle (mobile only) */}
            <div className="w-10 h-1.5 bg-zinc-200/80 rounded-full mx-auto mt-4 mb-2 md:hidden" />

            {/* Absolute close button so the title has full width breathing room */}
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              data-testid="feedback-close-btn"
              className="absolute top-5 right-5 md:top-7 md:right-7 w-10 h-10 rounded-full bg-zinc-50 hover:bg-zinc-100 flex items-center justify-center transition-colors text-zinc-500 hover:text-zinc-900 z-20"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="px-6 md:px-8 pt-6 md:pt-8 pb-2">
              <span className="uppercase text-[10px] tracking-[0.2em] text-[#FF5722] font-bold mb-2 block">Feedback</span>
              <h2 className="text-2xl md:text-3xl font-black text-zinc-900 tracking-tighter leading-none mt-2">Share feedback</h2>
              <p className="text-sm text-zinc-500 mt-3">We read every single one. Promise.</p>
            </div>

            {submitted ? (
              /* Thank-you */
              <div className="px-6 md:px-8 py-12 text-center animate-in zoom-in-95 duration-300">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-3">
                  <CheckCircle2 size={32} className="text-green-600" />
                </div>
                <div className="text-2xl font-black text-zinc-900 tracking-tight">Thank you! 🙏</div>
                <p className="text-sm text-zinc-500 mt-2">Your feedback reached the team.</p>
                <button
                  type="button"
                  onClick={close}
                  data-testid="feedback-done-btn"
                  className="mt-6 inline-flex items-center px-8 py-3 bg-zinc-900 hover:bg-[#FF5722] text-white text-sm font-bold rounded-full transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Scrollable form area */}
                <form onSubmit={submit} className="px-6 md:px-8 overflow-y-auto pb-36 flex-1 flex flex-col gap-8 md:gap-10 mt-4">
                  {/* Stars */}
                  <div className="flex flex-col gap-3">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">How was your experience?</div>
                    <div className="flex justify-center gap-2 md:gap-3" data-testid="feedback-star-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          onClick={() => setRating(n)}
                          data-testid={`feedback-star-rating-${n}`}
                          aria-label={`Rate ${n} stars`}
                          className="w-12 h-12 flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                        >
                          <Star
                            size={32}
                            fill={(hover || rating) >= n ? "#FF5722" : "transparent"}
                            className={(hover || rating) >= n ? "text-[#FF5722]" : "text-zinc-200"}
                            strokeWidth={1.5}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category */}
                  <div className="flex flex-col gap-3">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Category</div>
                    <div className="flex overflow-x-auto gap-2.5 pb-1 -mx-6 md:-mx-8 px-6 md:px-8 no-scrollbar">
                      {CATEGORIES.map((c) => {
                        const active = category === c.key;
                        return (
                          <button
                            type="button"
                            key={c.key}
                            onClick={() => setCategory(c.key)}
                            data-testid={`feedback-category-chip-${c.key}`}
                            className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-semibold transition-colors border ${active ? "border-[#FF5722] bg-[#FF5722]/10 text-[#FF5722]" : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-3">
                    <label htmlFor="fb-msg" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Your message</label>
                    <textarea
                      id="fb-msg"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What can we improve? What did you love? Anything broken?"
                      data-testid="feedback-message-input"
                      className="w-full bg-white border border-zinc-200 shadow-sm rounded-2xl p-4 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/10 outline-none transition-all resize-none min-h-[120px]"
                    />
                  </div>

                  {/* Optional identity */}
                  {!user && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Name (optional)"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        data-testid="feedback-name-input"
                        className="w-full bg-white border border-zinc-200 shadow-sm rounded-2xl p-4 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/10 outline-none transition-all"
                      />
                      <input
                        type="email"
                        placeholder="Email (optional)"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        data-testid="feedback-email-input"
                        className="w-full bg-white border border-zinc-200 shadow-sm rounded-2xl p-4 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-[#FF5722] focus:ring-4 focus:ring-[#FF5722]/10 outline-none transition-all"
                      />
                    </div>
                  )}

                  {error && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="feedback-error">{error}</div>
                  )}
                </form>

                {/* Sticky CTA — gradient fade above + floating button inside */}
                <div className="pointer-events-none absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-white via-white/95 to-transparent" />
                <div className="absolute bottom-6 inset-x-6 md:inset-x-8 z-10">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="feedback-submit-btn"
                    className="w-full h-14 rounded-full bg-[#FF5722] hover:bg-[#E64A19] disabled:opacity-60 text-white text-lg font-bold tracking-tight shadow-[0_8px_24px_rgba(255,87,34,0.3)] hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
                  >
                    {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send feedback"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
